/* ==========================================================================
   Kate Barton - sticky add to bag
   --------------------------------------------------------------------------
   WHY THIS FILE EXISTS

   theme.js's own <product-sticky-bar> resolves the footer with:

       document.querySelector(".shopify-section--footer")

   Our footer is `shopify-section--kb-footer`, so that returns null, the
   observer throws on a null target, and the bar never became visible at all.

   WHAT THIS CHANGES, AND WHAT IT KEEPS

   Kept: the bar hides once the footer comes into view. That condition is in
   the theme for a reason - a bar pinned to the bottom of the viewport sits
   directly on top of the footer's menus and policy links, and someone reading
   those has stopped evaluating the product. Navigation wins over a CTA they
   have already scrolled past.

   Changed: the theme showed the bar only after the whole product FORM had
   scrolled above the viewport. This anchors to the buy button itself and shows
   the bar whenever that button is off screen - including before it is reached,
   which matters on mobile where it often starts below the fold.

   So: visible when the buy button is off screen AND the footer is not in view.

   The footer is found by element, not by class. Both this theme's footers
   render as <footer>, so the lookup cannot silently miss the way the class
   selector did.

   HOW THE OVERRIDE TAKES EFFECT
   theme.js registers its version behind a guard:

       if (!window.customElements.get("product-sticky-bar")) { ... }

   so whoever defines the element first wins. theme.js is a module and modules
   are always deferred; this file loads as a classic script before it, runs
   during parsing and claims the name. Nothing in theme.js is edited.

   The cost of running that early: the element upgrades mid-parse, and <main>
   is parsed before the footer group - so at that moment there is no <footer>
   in the document to observe. connectedCallback therefore waits for
   DOMContentLoaded before resolving anything.
   ========================================================================== */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('product-sticky-bar')) return;

  class KateBartonProductStickyBar extends HTMLElement {
    constructor() {
      super();
      this._buttonIsOffScreen = false;
      this._footerIsVisible = false;
    }

    connectedCallback() {
      // TIMING. This file is a classic script so that it can claim the element
      // name before theme.js's deferred module - but that means the element
      // upgrades DURING parsing, and <main> is parsed before the footer group.
      // Looking for the footer here would find nothing. theme.js never hit
      // this because a deferred module runs after the document is parsed.
      if (document.readyState === 'loading') {
        const self = this;
        document.addEventListener(
          'DOMContentLoaded',
          function () {
            if (self.isConnected) self._watch();
          },
          { once: true }
        );
        return;
      }

      this._watch();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    /**
     * The buy button rendered in the page, not the copy inside this bar.
     * Falls back to the product form so the bar degrades to the theme's own
     * anchor rather than breaking, if <buy-buttons> ever goes away.
     */
    _buttonTarget() {
      const embedded = Array.prototype.slice
        .call(document.querySelectorAll('buy-buttons'))
        .find(function (el) {
          return !el.closest('product-sticky-bar');
        });

      return embedded || document.forms[this.getAttribute('form')] || null;
    }

    /**
     * By element, not by class. kb-footer and the stock footer both render as
     * <footer>, so this keeps working through a rename - which is exactly what
     * broke the theme's version.
     */
    _footerTarget() {
      return document.querySelector('footer, .shopify-section--footer');
    }

    _apply() {
      this.classList.toggle('is-visible', this._buttonIsOffScreen && !this._footerIsVisible);
    }

    _watch() {
      if (this._observer) this._observer.disconnect();

      const button = this._buttonTarget();
      const footer = this._footerTarget();

      // Nothing to anchor to: keep the bar hidden rather than stuck on screen.
      if (!button) {
        this.classList.remove('is-visible');
        return;
      }

      const self = this;

      this._observer = new IntersectionObserver(
        function (entries) {
          for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];

            // The info column re-renders on variant change, which can swap the
            // button for a new node. A detached element can never intersect,
            // which would pin the bar open - so re-acquire rather than trust a
            // stale target.
            if (!entry.target.isConnected) {
              self._watch();
              return;
            }

            if (entry.target === button) {
              self._buttonIsOffScreen = !entry.isIntersecting;
            } else if (entry.target === footer) {
              self._footerIsVisible = entry.isIntersecting;
            }
          }

          self._apply();
        },
        { threshold: 0 }
      );

      this._observer.observe(button);

      // No footer on the page is not a failure - the bar simply never has a
      // reason to hide. Unlike the theme, this does not observe a null target.
      if (footer) {
        this._observer.observe(footer);
      } else {
        this._footerIsVisible = false;
      }
    }
  }

  window.customElements.define('product-sticky-bar', KateBartonProductStickyBar);
})();
